import { Box, Button, Card, CardContent, Link, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { TotpEnrollmentKey } from "../components/two-factor/totp-enrollment-key.js";
import { api } from "../lib/api.js";
import { upstreamErrorMessage } from "../lib/upstreamError.js";

/**
 * Mandatory two-factor enrollment gate.
 *
 * Reached when a signed-in staff account has no second factor: the server
 * stamps `must_enroll` on the token and refuses every route except the
 * auth + self-service enrollment surface (plugins/auth.ts), and
 * `RequireAuth` bounces the user here. They pick a second factor — an
 * authenticator app (TOTP) OR email codes — enroll it, then we mint a
 * fresh token (so the server gate clears) and return to the dashboard.
 *
 * Email is offered as a first-class choice here (not just on /profile) so a
 * user who can't use an authenticator app — or who just disabled TOTP and
 * got bounced back — isn't trapped in an authenticator-only loop. The email
 * path needs no shared secret: portal-api emails a 6-digit code on demand.
 */
type Method = "totp" | "email";

export default function Setup2faPage(): JSX.Element {
  const [method, setMethod] = useState<Method | null>(null);

  return (
    <Shell>
      {method === "email" ? (
        <EmailEnrollment onBack={() => setMethod(null)} />
      ) : method === "totp" ? (
        <TotpEnrollment onBack={() => setMethod(null)} />
      ) : (
        <MethodChooser onPick={setMethod} />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }): JSX.Element {
  const { logout } = useAuth();
  return (
    <Box
      component="main"
      sx={{
        display: "grid",
        minHeight: "100vh",
        placeItems: "center",
        bgcolor: "background.default",
        px: 3,
        py: 5,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: "28rem", p: 3.5 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}
          >
            Two-factor authentication required
          </Typography>
          <Typography component="p" sx={{ mt: 0.75, fontSize: 14, color: "text.secondary" }}>
            Every staff account must protect the internal dashboard with a
            second factor before continuing.
          </Typography>

          <Box sx={{ mt: 3 }}>{children}</Box>

          <Box
            component="button"
            type="button"
            onClick={() => void logout()}
            sx={{
              mt: 3,
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              fontSize: 12,
              textDecoration: "underline",
              color: "text.disabled",
              "&:hover": { color: "text.primary" },
            }}
          >
            Sign out
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function MethodChooser({ onPick }: { onPick: (m: Method) => void }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        Choose how you'd like to receive your second factor:
      </Typography>
      <Button variant="contained" disableElevation onClick={() => onPick("totp")}>
        Use an authenticator app
      </Button>
      <Button variant="outlined" onClick={() => onPick("email")}>
        Email me a code instead
      </Button>
      <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
        Authenticator apps (1Password, Google Authenticator…) work offline.
        Email codes are sent to your work address each time you sign in.
      </Typography>
    </Box>
  );
}

function ErrorBanner({ message }: { message: string }): JSX.Element {
  return (
    <Typography
      role="alert"
      sx={{
        mt: 2,
        borderRadius: 1,
        border: 1,
        borderColor: (t) => alpha(t.palette.error.main, 0.4),
        bgcolor: (t) => alpha(t.palette.error.main, 0.1),
        px: 1.5,
        py: 1,
        fontSize: 12,
        color: "error.main",
      }}
    >
      {message}
    </Typography>
  );
}

function BackLink({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <Link
      component="button"
      type="button"
      underline="always"
      onClick={onBack}
      sx={{ display: "block", mt: 2, fontSize: 12, color: "text.secondary", textAlign: "left" }}
    >
      ← Choose a different method
    </Link>
  );
}

/** Shared "you're enrolled, here's how you continue" affordance. */
function useContinue(): { done: () => Promise<void>; continuing: boolean; error: string | null } {
  const { reauthenticate } = useAuth();
  const navigate = useNavigate();
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function done(): Promise<void> {
    setContinuing(true);
    setError(null);
    try {
      await reauthenticate();
      navigate("/", { replace: true });
    } catch (e) {
      setError(
        upstreamErrorMessage(e, "Enrolled, but couldn't refresh your session — sign in again."),
      );
      setContinuing(false);
    }
  }
  return { done, continuing, error };
}

function TotpEnrollment({ onBack }: { onBack: () => void }): JSX.Element {
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cont = useContinue();

  async function begin(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ secret: string; otpauth_url: string }>(
        "/api/profile/2fa/enable",
        { method: "POST", body: {} },
      );
      setSecret(r.secret);
      setOtpauth(r.otpauth_url);
    } catch (e) {
      setError(upstreamErrorMessage(e, "Could not start enrollment."));
    } finally {
      setBusy(false);
    }
  }

  async function verify(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ backup_codes?: string[] }>(
        "/api/profile/2fa/verify",
        { method: "POST", body: { otp: otp.trim() } },
      );
      setBackupCodes(r.backup_codes ?? []);
    } catch (e) {
      setError(upstreamErrorMessage(e, "That code didn't verify. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (backupCodes) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft ?? t.palette.background.default, 0.4),
            p: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary" }}>
            Two-factor is on. Save these one-time backup codes somewhere safe:
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 1,
              whiteSpace: "pre-wrap",
              fontFamily: "ui-monospace, monospace",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {backupCodes.join("  ")}
          </Box>
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={() => void cont.done()}
          disabled={cont.continuing}
        >
          {cont.continuing ? "Finishing…" : "Continue to dashboard"}
        </Button>
        {cont.error && <ErrorBanner message={cont.error} />}
      </Box>
    );
  }

  if (!secret) {
    return (
      <Box>
        <Button variant="contained" disableElevation onClick={() => void begin()} disabled={busy}>
          {busy ? "Preparing…" : "Set up authenticator"}
        </Button>
        {error && <ErrorBanner message={error} />}
        <BackLink onBack={onBack} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TotpEnrollmentKey secret={secret} otpauthUrl={otpauth} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="6-digit code"
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
          size="small"
          sx={{ width: 144 }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={() => void verify()}
          disabled={!otp || busy}
        >
          {busy ? "Verifying…" : "Verify & enable"}
        </Button>
      </Box>
      {error && <ErrorBanner message={error} />}
      <BackLink onBack={onBack} />
    </Box>
  );
}

function EmailEnrollment({ onBack }: { onBack: () => void }): JSX.Element {
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cont = useContinue();

  async function sendCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ email?: string }>("/api/profile/2fa/email/setup", {
        method: "POST",
        body: {},
      });
      setSentTo(r.email ?? null);
      setSent(true);
    } catch (e) {
      setError(upstreamErrorMessage(e, "Could not send a code. Try again."));
    } finally {
      setBusy(false);
    }
  }

  async function verify(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api("/api/profile/2fa/email/verify-setup", {
        method: "POST",
        body: { otp: otp.trim() },
      });
      setEnabled(true);
    } catch (e) {
      setError(upstreamErrorMessage(e, "That code didn't verify. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (enabled) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: "success.main" }}>
          ● Email codes are on. You'll get a 6-digit code by email each time you
          sign in.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => void cont.done()}
          disabled={cont.continuing}
        >
          {cont.continuing ? "Finishing…" : "Continue to dashboard"}
        </Button>
        {cont.error && <ErrorBanner message={cont.error} />}
      </Box>
    );
  }

  if (!sent) {
    return (
      <Box>
        <Typography sx={{ mb: 1.5, fontSize: 12, color: "text.secondary" }}>
          We'll email a 6-digit code to your work address to confirm you can
          receive it. You'll then get a fresh code each time you sign in.
        </Typography>
        <Button variant="contained" disableElevation onClick={() => void sendCode()} disabled={busy}>
          {busy ? "Sending…" : "Send code to my email"}
        </Button>
        {error && <ErrorBanner message={error} />}
        <BackLink onBack={onBack} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        Enter the 6-digit code we just emailed{sentTo ? ` to ${sentTo}` : ""}:
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="6-digit code"
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
          size="small"
          sx={{ width: 144 }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={() => void verify()}
          disabled={!otp || busy}
        >
          {busy ? "Verifying…" : "Verify & enable"}
        </Button>
      </Box>
      <Link
        component="button"
        type="button"
        underline="always"
        onClick={() => void sendCode()}
        sx={{ fontSize: 12, color: "primary.main", textAlign: "left" }}
      >
        Resend code
      </Link>
      {error && <ErrorBanner message={error} />}
      <BackLink onBack={onBack} />
    </Box>
  );
}
